// Reusable video upload flow popup: uploading progress, invalid-file message, and thumbnail selection/save actions.
import React from "react";

export type VideoUploadFlowPhase = "uploading" | "selecting" | "error";

type Props = {
  isVisible: boolean;
  phase: VideoUploadFlowPhase;
  progress: number;
  currentStep: string;
  canCancel: boolean;
  errorMessage?: string;
  thumbnails: string[];
  selectedIndex: number;
  isSaving: boolean;
  labels: {
    close: string;
    save: string;
    uploading: string;
    thumbnailTitle: string;
  };
  onCancelUpload?: () => void;
  onClose: () => void;
  onSave?: () => void;
  onSelectThumbnail?: (index: number) => void;
};

const VideoUploadFlowPopup = (props: Props) => {
  const {
    isVisible,
    phase,
    progress,
    currentStep,
    canCancel,
    errorMessage,
    thumbnails,
    selectedIndex,
    isSaving,
    labels,
    onCancelUpload,
    onClose,
    onSave,
    onSelectThumbnail,
  } = props;

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="h-2 w-full bg-gray-200">
          <div
            className="h-full bg-[#CF3688] transition-all duration-300"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>

        {phase === "uploading" ? (
          <div className="relative p-5">
            {canCancel && onCancelUpload ? (
              <button
                type="button"
                onClick={onCancelUpload}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                aria-label={labels.close}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            ) : null}
            <div className="flex min-h-16 items-center justify-center">
              <p className="text-sm font-medium text-gray-700">
                {currentStep || labels.uploading}
              </p>
            </div>
          </div>
        ) : null}

        {phase === "error" ? (
          <div className="p-5">
            <p className="text-center text-sm font-medium text-gray-700">
              {errorMessage || labels.uploading}
            </p>
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black"
              >
                {labels.close}
              </button>
            </div>
          </div>
        ) : null}

        {phase === "selecting" ? (
          <div className="p-4 sm:p-5">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">
              {labels.thumbnailTitle}
            </h3>
            <div className="overflow-hidden rounded-xl bg-gray-100">
              <img
                src={thumbnails[selectedIndex] || "/imgs/common/video-thumbnail-empty.png"}
                alt=""
                className="h-64 w-full object-contain bg-black"
              />
            </div>
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {thumbnails.map((src, idx) => (
                <button
                  key={`${src.slice(0, 24)}-${idx}`}
                  type="button"
                  onClick={() => onSelectThumbnail?.(idx)}
                  className={`h-16 w-28 flex-shrink-0 overflow-hidden rounded-lg border-2 ${
                    idx === selectedIndex ? "border-[#CF3688]" : "border-transparent"
                  }`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {labels.close}
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={isSaving || thumbnails.length === 0}
                className="rounded-lg bg-[#CF3688] px-4 py-2 text-sm font-medium text-white hover:bg-[#B82F77] disabled:opacity-50"
              >
                {labels.save}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default VideoUploadFlowPopup;
